import { gql } from '@apollo/client'
import { navigate } from '@reach/router'
import * as React from 'react'
import styled from 'styled-components'
import Icon from '../../../../common/src/components/Icon'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import { IntroTextSpan, BodyTextSpan, IntroText } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { dateMDYFmt } from '../../../../common/src/date'
import { KeyAction } from '../../../../common/src/keyboard'
import { FetchSearchQuery, SearchResultType, FetchSearchQueryVariables } from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'

export const fetchSearchQuery = gql`
  query FetchSearch($prefix: String!) {
    search(prefix: $prefix) {
      type
      path
      displayName
      snippet
      deemphasize
      timeUpdated
    }
  }
`
interface OmniSearchProps {
  onFocus: () => void
  onBlur: () => void
}

export default class OmniSearch extends React.PureComponent<OmniSearchProps> {
  render() {
    const { onFocus, onBlur } = this.props

    return (
      <TypeaheadBox<FetchSearchQuery['search'][0]>
        loadFunc={this.search}
        rowFunc={this.renderSearch}
        onChoose={this.handleSelectSearch}
        onFocus={onFocus}
        onBlur={onBlur}
        focusAction={KeyAction.search}
        fadeBackground
      />
    )
  }

  private search = (prefix: string) =>
    apolloWebClient
      .query<FetchSearchQuery, FetchSearchQueryVariables>({
        query: fetchSearchQuery,
        variables: { prefix },
        fetchPolicy: 'network-only',
      })
      .then(res => res.data.search)
      .catch(err => {
        console.error(err)
        return []
      })

  private renderSearch = (row: FetchSearchQuery['search'][0]) => {
    const { path, type, displayName, snippet, deemphasize, timeUpdated } = row
    return (
      <ResultRow
        key={path}
        path={path}
        type={type}
        displayName={displayName}
        snippet={snippet}
        deemphasize={deemphasize}
        timeUpdated={timeUpdated}
        data-testid={`search-result-${path}`}
      />
    )
  }

  private handleSelectSearch = (row: FetchSearchQuery['search'][0]) => {
    navigate(row.path).catch(handleError)
  }
}

interface ResultRowProps {
  type: SearchResultType
  path: string
  displayName: string
  snippet: string
  deemphasize: boolean
  timeUpdated: string
}

export function ResultRow(props: ResultRowProps) {
  const { type, path, displayName, snippet, deemphasize, timeUpdated } = props

  let typeIcon

  switch (type) {
    case 'TASK_SINGLE_FAMILY':
      typeIcon = <Icon name="task-single-family" />
      break

    case 'TASK_MULTI_FAMILY':
      typeIcon = <Icon name="task-multi-family" />
      break

    case 'TASK_STUDENT':
      typeIcon = <Icon name="task-student-housing" />
      break

    case 'TASK_SENIOR':
      typeIcon = <Icon name="task-senior-housing" />
      break

    case 'TASK_FIRM':
      typeIcon = <Icon name="task-firm" />
      break

    case 'TASK':
      typeIcon = <Icon name="task-generic" />
      break

    case 'THREAD_BOSS':
      typeIcon = <Icon name="contact-boss" />
      break

    case 'THREAD_AGENT':
      typeIcon = <Icon name="contact-agent" />
      break

    case 'THREAD_PROSPECT':
    case 'THREAD':
      typeIcon = ''
      break

    default:
      typeIcon = ''
  }

  const mainColor = deemphasize ? 'midway' : 'silver'

  let snippetElem
  const ix = Math.max(snippet.indexOf('📞'), snippet.indexOf('✉️'))
  if (ix >= 0) {
    snippetElem = (
      <span>
        {snippet.substring(0, ix)}
        <Icon size="small" name={snippet.includes('📞') ? 'channel-sms' : 'channel-email'} />
        {snippet.substring(ix + 2)}
      </span>
    )
  } else {
    snippetElem = snippet
  }

  return (
    <RowRoot>
      <RowTypeIcon color={mainColor}>{typeIcon}</RowTypeIcon>
      <RowLeft>
        <IntroText color={mainColor}>
          <DisplayNameContainer href={path}>{displayName}</DisplayNameContainer>
        </IntroText>
      </RowLeft>
      <RowMid>
        <BodyTextSpan>{snippetElem}</BodyTextSpan>
      </RowMid>
      <RowRight>
        <BodyTextSpan>{dateMDYFmt(timeUpdated)}</BodyTextSpan>
      </RowRight>
    </RowRoot>
  )
}

const DisplayNameContainer = styled.a`
  color: inherit;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const RowRoot = styled.div`
  display: flex;
  align-items: baseline;
  flex-direction: row;
  height: 24px;
`

const RowTypeIcon = styled(IntroTextSpan)`
  vertical-align: baseline;
  flex-grow: 0;
  width: 32px;
  min-width: 24px;
`

const RowLeft = styled.div`
  vertical-align: baseline;
  flex-grow: 0;
  white-space: nowrap;
`

const RowMid = styled.div`
  vertical-align: baseline;
  flex-grow: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: left;
  margin: 0 16px;
`

const RowRight = styled.div`
  vertical-align: baseline;
  flex-grow: 0;
`
